'use client';

import React, { useRef, useState } from 'react';
import { faHospital } from '@fortawesome/pro-solid-svg-icons';
import {
  useGetAllStrategiesSiteOfCareTypesQuery,
} from 'src/api/redux/strategy/allStrategiesApi';
import {
  buildSocCombinedOptions,
  splitSiteOfCareValues,
  mergeSocRowsToOptions,
} from 'src/components/FilterSets/SiteOfCare/utils';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { useLogging } from 'src/context/logging';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import {
  FilterDropdownButton,
  FilterDropdownPortal,
  CheckboxListItem,
  DropdownEmptyState,
} from '../shared';

const StrategySiteOfCareTypeDropdown: React.FC = (): React.ReactElement => {
  const log = useLogging();
  const {
    boundedTerritory,
    selectedTerritory,
    selectedChainIds,
    selectedSocTypes,
    setSelectedSocTypes,
    selectedSocSubtypes,
    setSelectedSocSubtypes,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: socTypes, isFetching } =
    useGetAllStrategiesSiteOfCareTypesQuery(
      {
        chain_id: selectedChainIds,
        territory_id: selectedTerritory?.id,
        bounding_territory: boundedTerritory?.id,
      },
      { skip: selectedChainIds.length === 0 }
    );

  const allTypes = socTypes?.results ?? [];

  // Flatten the nested type/subtype structure into combined rows
  // ("Hospital", "Hospital > Acute Care") matching the discovery filter.
  const allOptions = buildSocCombinedOptions(allTypes);

  const query = search.trim().toLowerCase();
  const filteredOptions = query
    ? allOptions.filter((option) => option.label.toLowerCase().includes(query))
    : allOptions;

  // Reconstruct the selected combined rows from the two stored arrays so the
  // checkboxes reflect current state (a selected subtype collapses its parent).
  const selectedOptions = mergeSocRowsToOptions(
    selectedSocTypes,
    selectedSocSubtypes,
    allTypes
  );
  const selectedValues = new Set(selectedOptions.map((option) => option.value));
  const totalSelectedCount = selectedOptions.length;

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    300
  );

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useResizeClose(() => setIsOpen(false), isOpen);

  const isDisabled = selectedChainIds.length === 0;

  const handleClick = () => {
    if (isDisabled) return;
    if (!isOpen) calculatePosition();
    setIsOpen(!isOpen);
  };

  const commitSelection = (nextOptions: typeof selectedOptions) => {
    const { socType, socSubtype } = splitSiteOfCareValues(nextOptions);
    setSelectedSocTypes(socType);
    setSelectedSocSubtypes(socSubtype);
  };

  const handleToggle = (value: string) => {
    const isSelected = selectedValues.has(value);
    const option = allOptions.find((candidate) => candidate.value === value);
    if (!option) return;

    const nextOptions = isSelected
      ? selectedOptions.filter((candidate) => candidate.value !== value)
      : [...selectedOptions, option];
    commitSelection(nextOptions);

    log.event('Strategy filter site of care type toggled', {
      source: 'StrategySiteOfCareTypeDropdown',
      socOption: value,
      socOptionLabel: option.label,
      action: isSelected ? 'removed' : 'added',
      totalSelected: nextOptions.length,
      selectedChainIds,
    });
  };

  const handleClearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const previousCount = totalSelectedCount;
    setSelectedSocTypes([]);
    setSelectedSocSubtypes([]);

    log.event('Strategy filter site of care types cleared', {
      source: 'StrategySiteOfCareTypeDropdown',
      clearedCount: previousCount,
      selectedChainIds,
    });
  };

  const getDisplayText = () => {
    if (totalSelectedCount === 0) return 'Site of Care Types';
    if (totalSelectedCount === 1) return selectedOptions[0].label;
    return `${totalSelectedCount} Site of Care Types`;
  };

  return (
    <>
      <FilterDropdownButton
        icon={faHospital}
        label="Site of Care Type"
        isOpen={isOpen}
        isDisabled={isDisabled}
        displayText={getDisplayText()}
        hasSelection={totalSelectedCount > 0}
        onClear={handleClearAll}
        onClick={handleClick}
        buttonRef={buttonRef}
        testId="strategy-site-of-care-type-dropdown"
      />

      <FilterDropdownPortal
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        scrollContainerRef={scrollContainerRef}
        dropdownStyle={dropdownStyle}
        searchPlaceholder="Search site of care types..."
        searchValue={search}
        onSearchChange={setSearch}
        selectedCount={totalSelectedCount}
        onClearAll={() => handleClearAll()}
      >
        {filteredOptions.map((option) => (
          <CheckboxListItem
            key={option.value}
            id={option.value}
            label={option.label}
            isSelected={selectedValues.has(option.value)}
            onToggle={handleToggle}
          />
        ))}

        <DropdownEmptyState
          hasResults={filteredOptions.length > 0}
          isLoading={isFetching}
          searchTerm={search}
          emptyMessage="site of care types"
        />
      </FilterDropdownPortal>
    </>
  );
};

export default StrategySiteOfCareTypeDropdown;
